/**
 * Lazy-init for below-the-fold carousels/accordions.
 *
 * The compiled theme bundle (sdg-app.js/sdg-theme.js) constructs all of these
 * synchronously at page load regardless of scroll position. Each block below
 * defers that work until the element is within 200px of the viewport, then
 * calls the same already-global APIs (Flickity / SDG.accordion) the bundle
 * itself uses, or — where the bundle's own section framework already supports
 * on-demand (re-)construction via `shopify:section:load` — replays that same
 * event once visible instead of duplicating the framework's own logic.
 *
 * Groups 1/2/3a/3b/3c (carousels + section-framework re-renders) share one
 * scheduler (LazyBelowFold.schedule) instead of calling activate() straight
 * from their IntersectionObserver callback. On a fast scroll, several sections
 * cross the 200px rootMargin within the same frame, and running every group's
 * construction (Flickity, shopify:section:load re-renders) synchronously in
 * that one callback is what showed up as mobile TBT spikes on Home/Cart — many
 * below-fold sections, one burst. The shared queue runs one activation per idle
 * slot instead.
 *
 * Group 4 (accordions) is deliberately NOT on this scheduler — see its own
 * comment below.
 */
window.LazyBelowFold = (function () {
  var queue = [];
  var scheduled = false;

  var runIdle = window.requestIdleCallback || function (fn) {
    return setTimeout(function () { fn({ timeRemaining: function () { return 0; }, didTimeout: true }); }, 1);
  };

  function flush(deadline) {
    scheduled = false;
    while (queue.length && (deadline.didTimeout || deadline.timeRemaining() > 0)) {
      queue.shift()();
    }
    if (queue.length) schedule();
  }

  function schedule() {
    if (scheduled) return;
    scheduled = true;
    runIdle(flush, { timeout: 300 });
  }

  return {
    schedule: function (fn) {
      queue.push(fn);
      schedule();
    }
  };
})();

// --- Group 1: sections driven by the bundle's SectionContainer framework ---
// (data-section-type is temporarily suppressed as data-section-type-lazy in
// Liquid so the framework's own eager per-type scan finds nothing at load.)
(function () {
  function activate(el) {
    var realType = el.getAttribute('data-section-type-lazy');
    el.setAttribute('data-section-type', realType);
    el.removeAttribute('data-section-type-lazy');
    var wrapper = el.closest('.shopify-section') || el;
    wrapper.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
  }

  function observe(scope) {
    var els = (scope || document).querySelectorAll('[data-section-type-lazy]:not([data-section-type-observed])');
    if (!els.length) return;

    if (!window.IntersectionObserver) {
      els.forEach(function (el) { el.dataset.sectionTypeObserved = '1'; activate(el); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          io.unobserve(entry.target);
          window.LazyBelowFold.schedule(function () { activate(entry.target); });
        }
      });
    }, { rootMargin: '200px' });

    els.forEach(function (el) { el.dataset.sectionTypeObserved = '1'; io.observe(el); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { observe(document); });
  } else {
    observe(document);
  }

  document.addEventListener('shopify:section:load', function (evt) { observe(evt.target); });
})();

// --- Group 2: bare data-flickity attribute (native auto-init, config already inline) ---
(function () {
  function activate(el) {
    if (!window.Flickity) return;
    var opts;
    try {
      opts = JSON.parse(el.getAttribute('data-flickity-lazy'));
    } catch (e) {
      opts = {};
    }
    new window.Flickity(el, opts);
  }

  function observe(scope) {
    var els = (scope || document).querySelectorAll('[data-flickity-lazy]:not([data-flickity-lazy-observed])');
    if (!els.length) return;

    if (!window.IntersectionObserver) {
      els.forEach(function (el) { el.dataset.flickityLazyObserved = '1'; activate(el); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          io.unobserve(entry.target);
          window.LazyBelowFold.schedule(function () { activate(entry.target); });
        }
      });
    }, { rootMargin: '200px' });

    els.forEach(function (el) { el.dataset.flickityLazyObserved = '1'; io.observe(el); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { observe(document); });
  } else {
    observe(document);
  }

  document.addEventListener('shopify:section:load', function (evt) { observe(evt.target); });
})();

// --- Group 3a: gift-set-details carousel (mobile-only, matches bundle's create/destroy-on-breakpoint) ---
(function () {
  var mq = window.matchMedia('(max-width: 1007px)');

  function build(el) {
    if (!window.Flickity) return;
    var existing = window.Flickity.data(el);
    if (existing) existing.destroy();
    // matches the bundle's own 500ms delay before constructing
    setTimeout(function () {
      if (mq.matches) new window.Flickity(el, { pageDots: true, prevNextButtons: false });
    }, 500);
  }

  function destroy(el) {
    var existing = window.Flickity && window.Flickity.data(el);
    if (existing) existing.destroy();
  }

  function activate(el) {
    if (mq.matches) build(el);
    mq.addEventListener('change', function (e) {
      if (e.matches) build(el); else destroy(el);
    });
  }

  function observe(scope) {
    var els = (scope || document).querySelectorAll('.js-gift-set-details-carousel-lazy:not([data-carousel-observed])');
    if (!els.length) return;

    if (!window.IntersectionObserver) {
      els.forEach(function (el) { el.dataset.carouselObserved = '1'; activate(el); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          io.unobserve(entry.target);
          window.LazyBelowFold.schedule(function () { activate(entry.target); });
        }
      });
    }, { rootMargin: '200px' });

    els.forEach(function (el) { el.dataset.carouselObserved = '1'; io.observe(el); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { observe(document); });
  } else {
    observe(document);
  }

  document.addEventListener('shopify:section:load', function (evt) { observe(evt.target); });
})();

// --- Group 3b: .js-results-carousel (product-results / product-callouts / product-callouts-v2) ---
// Also replaces those sections' 3 duplicated inline fade scripts, which each queried
// `.js-results-carousel` singularly and broke when more than one instance shared a page.
(function () {
  function activate(el) {
    if (!window.Flickity) return;

    var opts = { pageDots: true, prevNextButtons: false, wrapAround: true };
    if (el.getAttribute('data-two-across') === 'true') opts.cellAlign = 'left';
    var totalSlides = el.getAttribute('data-total-slides');
    if (totalSlides && +totalSlides < 3) {
      opts.wrapAround = false;
      opts.contain = true;
    }

    var carousel = new window.Flickity(el, opts);
    var parent = el.parentElement;
    if (parent) {
      parent.addEventListener('click', function (evt) {
        if (!evt.target.closest) return;
        if (evt.target.closest('.js-next')) carousel.next();
        else if (evt.target.closest('.js-prev')) carousel.previous();
      });
    }

    initFade(el);
    initVideoPlay(el);
  }

  // Replacement for the bundle's document-level video-play-button binding, which is a
  // one-time `querySelectorAll('.js-results-carousel .js-video-play-btn')` done at page
  // load — it would never find buttons nested in a renamed (later-activated) carousel.
  // Simplified vs. the original: plays this carousel's video without pausing unrelated
  // videos elsewhere on the page (the original's sitewide "stop all others" behavior).
  function initVideoPlay(carousel) {
    carousel.addEventListener('click', function (evt) {
      var playBtn = evt.target.closest && evt.target.closest('.js-video-play-btn');
      if (!playBtn) return;

      var wrapper = playBtn.parentElement;
      var poster = wrapper.querySelector('.js-video-poster');
      var videoWrapper = wrapper.querySelector('.js-video-wrapper');
      if (!videoWrapper) return;

      playBtn.classList.add('hide');
      if (poster) poster.classList.add('hide');
      videoWrapper.classList.remove('hide');

      if (playBtn.dataset.vidSrc === 'youtube_vimeo_src') {
        var iframe = videoWrapper.querySelector('iframe');
        if (iframe) {
          iframe.muted = true;
          iframe.src = (iframe.getAttribute('data-src') || '') + '&autoplay=1';
        }
      } else {
        var video = videoWrapper.querySelector('video');
        if (video) {
          setTimeout(function () {
            video.play();
            video.loop = true;
          }, 400);
        }
      }
    });
  }

  // Consolidated, per-instance replacement for product-callouts.liquid's and
  // product-callouts-v2.liquid's identical fade-on-scroll effect (previously two
  // separate inline scripts, each bound via a singular, first-match-only
  // document.querySelector('.js-results-carousel') — broken whenever more than
  // one instance rendered on the same page, e.g. product.total-set.json).
  // Math unchanged from the original inline scripts.
  function initFade(carousel) {
    var fadeStartRatio = 0.73;
    var fadeEndRatio = 0.89;
    var fadeCompression = 3;
    var fadeSharpness = 8;
    var minOpacity = 0.0;
    var maxOpacity = 1.0;

    var texts = carousel.querySelectorAll('.Product-Results__carousel-item > div > span:not(.tw-dot-glow)');
    if (!texts.length) return;

    function updateFade() {
      var carouselRect = carousel.getBoundingClientRect();
      var fadeStartX = carouselRect.width * fadeStartRatio;
      var fadeEndX = carouselRect.width * fadeEndRatio;
      var fadeWidth = (fadeEndX - fadeStartX) / fadeCompression;

      texts.forEach(function (span) {
        var rect = span.getBoundingClientRect();
        var centerX = rect.left - carouselRect.left + rect.width / 2;
        var opacity = minOpacity;

        if (centerX >= fadeStartX - fadeWidth && centerX < fadeStartX) {
          opacity = Math.pow(1 - (fadeStartX - centerX) / fadeWidth, fadeSharpness);
        } else if (centerX >= fadeStartX && centerX <= fadeEndX) {
          opacity = maxOpacity;
        } else if (centerX > fadeEndX && centerX <= fadeEndX + fadeWidth) {
          opacity = Math.pow(1 - (centerX - fadeEndX) / fadeWidth, fadeSharpness);
        }

        span.style.transition = 'none';
        span.style.opacity = opacity;
      });
    }

    var flkty = window.Flickity && window.Flickity.data(carousel);
    if (flkty) flkty.on('scroll', updateFade);
    else carousel.addEventListener('scroll', updateFade);

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(updateFade, 200);
    });
    updateFade();
  }

  function observe(scope) {
    var els = (scope || document).querySelectorAll('.js-results-carousel-lazy:not([data-results-carousel-observed])');
    if (!els.length) return;

    if (!window.IntersectionObserver) {
      els.forEach(function (el) { el.dataset.resultsCarouselObserved = '1'; activate(el); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          io.unobserve(entry.target);
          window.LazyBelowFold.schedule(function () { activate(entry.target); });
        }
      });
    }, { rootMargin: '200px' });

    els.forEach(function (el) { el.dataset.resultsCarouselObserved = '1'; io.observe(el); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { observe(document); });
  } else {
    observe(document);
  }

  document.addEventListener('shopify:section:load', function (evt) { observe(evt.target); });
})();

// --- Group 3c: homepage-review-carousel (.js-review-carousel, breakpoint-dependent opts,
// plus its video-play-button and page-dot delegation — the bundle's whole module is
// gated on this one class, so all three behaviors move together) ---
(function () {
  var mq = window.matchMedia('(max-width: 1007px)');
  var DESKTOP_OPTS = { pageDots: false, prevNextButtons: true, imagesLoaded: true, draggable: false, groupCells: 3, percentPosition: true };
  var MOBILE_OPTS = { pageDots: false, prevNextButtons: false, imagesLoaded: true, draggable: true, initialIndex: 1, percentPosition: false, cellAlign: 'center' };

  function activate(carousel) {
    if (!window.Flickity) return;

    var flickityCarousel;
    var activePlayBtn;
    var carouselDots = carousel.parentElement.querySelectorAll('.js-carousel-dot');

    function pauseVideo() {
      var parent = activePlayBtn.parentElement;
      var poster = parent.querySelector('.js-video-poster');
      var wrapper = parent.querySelector('.js-video-wrapper');
      activePlayBtn.classList.remove('hide');
      if (poster) poster.classList.remove('hide');
      if (wrapper) wrapper.classList.add('hide');
      if (activePlayBtn.dataset.vidSrc === 'youtube_vimeo_src') {
        var iframe = parent.querySelector('iframe');
        if (iframe) { iframe.muted = true; iframe.src = ''; }
      } else {
        var video = parent.querySelector('video');
        if (video) video.pause();
      }
      activePlayBtn = null;
    }

    function playVideo(evt) {
      var playBtn = evt.target;
      var parent = playBtn.parentElement;
      if (activePlayBtn) pauseVideo();
      activePlayBtn = playBtn;
      var poster = parent.querySelector('.js-video-poster');
      var wrapper = parent.querySelector('.js-video-wrapper');
      playBtn.classList.add('hide');
      if (poster) poster.classList.add('hide');
      if (wrapper) wrapper.classList.remove('hide');
      if (playBtn.dataset.vidSrc === 'youtube_vimeo_src') {
        var iframe = parent.querySelector('iframe');
        if (iframe) {
          iframe.muted = true;
          iframe.src = (iframe.getAttribute('data-src') || '') + '&autoplay=1';
        }
      } else {
        var video = parent.querySelector('video');
        if (video) setTimeout(function () { video.play(); }, 400);
      }
    }

    function handleDotClick(evt) {
      var target = evt.target;
      if (target.dataset.btnSectionId !== carousel.dataset.sectionId) return;
      flickityCarousel.selectCell(parseInt(target.dataset.idx, 10));
    }

    function build(opts) {
      if (flickityCarousel) flickityCarousel.destroy();
      flickityCarousel = new window.Flickity(carousel, opts);
      flickityCarousel.on('select', function () {
        if (activePlayBtn) pauseVideo();
        carouselDots.forEach(function (dot) {
          dot.classList.toggle('is-selected', parseInt(dot.dataset.idx, 10) === flickityCarousel.selectedIndex);
        });
      });
    }

    build(mq.matches ? MOBILE_OPTS : DESKTOP_OPTS);
    mq.addEventListener('change', function () {
      build(mq.matches ? MOBILE_OPTS : DESKTOP_OPTS);
    });

    carousel.addEventListener('click', function (evt) {
      if (evt.target.classList.contains('js-video-play-btn')) playVideo(evt);
    });
    carousel.parentElement.addEventListener('click', function (evt) {
      if (evt.target.classList.contains('js-carousel-dot')) handleDotClick(evt);
    });
  }

  function observe(scope) {
    var els = (scope || document).querySelectorAll('.js-review-carousel-lazy:not([data-review-carousel-observed])');
    if (!els.length) return;

    if (!window.IntersectionObserver) {
      els.forEach(function (el) { el.dataset.reviewCarouselObserved = '1'; activate(el); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          io.unobserve(entry.target);
          window.LazyBelowFold.schedule(function () { activate(entry.target); });
        }
      });
    }, { rootMargin: '200px' });

    els.forEach(function (el) { el.dataset.reviewCarouselObserved = '1'; io.observe(el); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { observe(document); });
  } else {
    observe(document);
  }

  document.addEventListener('shopify:section:load', function (evt) { observe(evt.target); });
})();

// --- Group 4: accordions (footer, comparison-FAQ) ---
// Not routed through LazyBelowFold.schedule like the other groups: SDG.accordion's
// init() sets the collapsed/expanded state, which changes the element's height.
// Queuing that behind requestIdleCallback let the footer paint tall (pre-init) and
// then collapse a moment later, producing a real layout shift that didn't exist
// before (CLS 0.158 measured on Cart) — the opposite of what the July CLS fix this
// file exists for was for. Activate immediately on intersect, same as before.
(function () {
  function activate(el) {
    if (!window.SDG || !window.SDG.accordion) return;
    window.SDG.accordion({ dom: { id: el.id } }).init();
  }

  function observe(scope) {
    var els = (scope || document).querySelectorAll('.js-acc-lazy:not([data-acc-observed])');
    if (!els.length) return;

    if (!window.IntersectionObserver) {
      els.forEach(function (el) { el.dataset.accObserved = '1'; activate(el); });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          io.unobserve(entry.target);
          activate(entry.target);
        }
      });
    }, { rootMargin: '200px' });

    els.forEach(function (el) { el.dataset.accObserved = '1'; io.observe(el); });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { observe(document); });
  } else {
    observe(document);
  }

  document.addEventListener('shopify:section:load', function (evt) { observe(evt.target); });
})();
